import * as React from 'react';
import { passwordStrength } from 'check-password-strength';

const LEADING_TRAILING_SPACES_ERROR =
    'Leading and trailing spaces are not allowed. A non-leading, non-trailing space character is also treated as a special character.';
const INVALID_PASSWORD_ERROR =
    'Password must be at least 8 characters and contain uppercase letters, lowercase letters, numbers and symbols';

export const buildLabelRule = (entityName: string) => ({
    pattern: '^(?=.*[A-Za-z0-9])^([A-Za-z0-9_+ -.()]{4,})$',
    message: `${entityName} should be at least 4 characters long, and contain only alphanumeric, space and some special characters "+-_.()!#$%&*',". At least one alphanumeric character should be present.`,
});

const isStrengthPassword = (password: string): boolean => passwordStrength(password).id >= 2;

export const sleep = async (ms: number): Promise<any> => new Promise((resolve) => setTimeout(resolve, ms));

export const usePromiseMemo = <T>(defaultValue: T, callback: () => Promise<T>, deps: any[]) => {
    const [state, setState] = React.useState<T>(defaultValue);
    React.useEffect(() => {
        callback().then((result) => setState(result));
    }, deps);
    return state;
};

function checkPassword(password: string): string {
    const hasLeadingAndTrailingSpaces = new RegExp(/(^ +)|( +$)/).test(password);
    if (hasLeadingAndTrailingSpaces) return LEADING_TRAILING_SPACES_ERROR;
    if (password.length > 256) return 'Password is too long.';
    return undefined;
}

export const passwordValidator = (rule, value, callback) =>
    callback(checkPassword(value) ?? (isStrengthPassword(value) ? undefined : INVALID_PASSWORD_ERROR));

export const getDisplayCRC = (crcValue: string): string => {
    const crcTemplate = '00000000';
    return crcTemplate.substring(crcValue?.length || 0) + crcValue;
};

export function isStrictUrl(urlString: string): boolean {
    // 1. Immediately reject strings with whitespace
    if (/\s/.test(urlString)) {
        return false;
    }

    try {
        const url = new URL(urlString);

        // 2. Enforce http / https
        if (url.protocol !== "http:" && url.protocol !== "https:") {
            return false;
        }

        // 3. Reject invalid domain symbols (like $, %, # in host) and require a valid TLD
        const hostnameRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        return hostnameRegex.test(url.hostname);
    } catch {
        return false;
    }
}